import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, RotateCw } from "lucide-react";
import { AdminRole, FireblocksEventPublishStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, DateRangeFilterFields, enumOptions, SelectFilterField, TextFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { CopyButton } from "@/components/copy-button";
import { JsonViewer } from "@/components/json-viewer";
import { STATUS_DOT_COLOR, type StatusColor } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { ApiError } from "@/api/client";
import { getFireblocksEvent, listFireblocksEvents, retryFireblocksEvent } from "@/api/fireblocks-events";
import { type FireblocksEventListItem, type ListFireblocksEventsParams } from "@/api/types";

// Real implementation of Task 30 Part G, replacing the Task-22 placeholder:
// raw custody-provider events (TECH_VIEWER-gated). Real `useTableState` +
// `DataTable`, server-side pagination, no sort (the DTO has none).
// `eventType`/`resourceId` filters are plain text inputs, not `<select>`s —
// the backend DTO validates them as `@IsString()` (no fixed enum
// vocabulary), unlike webhook-deliveries' `eventType`.
//
// The plan calls the row detail "row expand → json-viewer", but `DataTable`
// has no expandable-row slot (checked its props — no such concept exists,
// extending it is out of this task's scope). This uses a fetch-on-open
// `Dialog` instead, same convention as every other detail view in this app,
// with the `JsonViewer` started expanded (`defaultCollapsed={false}`) since
// opening the dialog IS the "expand" action here — a deliberate, disclosed
// deviation from the literal wording, forced by DataTable's actual shape.
type FireblocksEventFilters = {
  eventType?: string;
  resourceId?: string;
  publishStatus?: FireblocksEventPublishStatus;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: FireblocksEventFilters = {};

/** Plain type-guarded passthrough — mirrors _app.invoices.tsx's `validateSearch` house style. */
function validateFireblocksEventsSearch(
  search: Record<string, unknown>,
): TableSearch<FireblocksEventFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    eventType: typeof search.eventType === "string" ? search.eventType : undefined,
    resourceId: typeof search.resourceId === "string" ? search.resourceId : undefined,
    publishStatus:
      typeof search.publishStatus === "string" ? (search.publishStatus as FireblocksEventPublishStatus) : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/fireblocks-events")({
  validateSearch: validateFireblocksEventsSearch,
  component: FireblocksEventsPage,
});

// ─── formatting helpers ─────────────────────────────────────────────────────

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function truncate(value: string): string {
  return value.length <= 16 ? value : `${value.slice(0, 8)}…${value.slice(-6)}`;
}

const EVENT_TYPE_COLORS: StatusColor[] = ["info", "purple", "warning", "success", "danger", "neutral"];

/**
 * `eventType` is a free-text Fireblocks event name with no fixed vocabulary
 * (see this file's top doc comment), so there's no enum to map to a color
 * the way `status-colors.ts` does for real status enums. Instead, hash the
 * raw string to a stable index into the shared palette — the same event
 * type always renders the same color, without maintaining a lookup table
 * that would go stale as Fireblocks adds event types.
 */
function eventTypeColor(eventType: string): StatusColor {
  let hash = 0;
  for (let i = 0; i < eventType.length; i++) {
    hash = (hash * 31 + eventType.charCodeAt(i)) | 0;
  }
  return EVENT_TYPE_COLORS[Math.abs(hash) % EVENT_TYPE_COLORS.length];
}

/** feature-041: the outbox state CPG reached for this event. FAILED is the stuck one. */
const PUBLISH_STATUS_COLOR: Record<FireblocksEventPublishStatus, StatusColor> = {
  [FireblocksEventPublishStatus.PENDING]: "info",
  [FireblocksEventPublishStatus.PROCESSING]: "info",
  [FireblocksEventPublishStatus.PUBLISHED]: "success",
  [FireblocksEventPublishStatus.SKIPPED]: "neutral",
  [FireblocksEventPublishStatus.FAILED]: "danger",
};

// ─── row-detail dialog ──────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

/** Uppercase eyebrow label above a group of `DetailRow`s — same treatment used across the app's other detail dialogs. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>;
}

/** Bordered card wrapper for a section — same treatment -transactions-table.tsx's detail dialog established. */
function SectionCard({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-3 rounded-md border border-border p-3", className)}>
      <SectionLabel>{title}</SectionLabel>
      {children}
    </div>
  );
}

/** A chronological event list rendered as a connected vertical stepper — genuinely sequential data, unlike a plain key/value list. */
function Timeline({ entries }: { entries: Array<{ label: string; value: string }> }) {
  return (
    <ol>
      {entries.map((entry, index) => (
        <li key={entry.label} className="relative flex gap-3 pb-4 pl-0.5 last:pb-0">
          {index < entries.length - 1 && (
            <span className="absolute left-[3.5px] top-3 h-full w-px bg-border" aria-hidden="true" />
          )}
          <span className="relative top-1.5 h-[7px] w-[7px] shrink-0 rounded-full bg-foreground/50" />
          <div className="flex flex-1 items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{entry.label}</span>
            <span className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
              {entry.value}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function retryErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return "This event has already been retried.";
  if (err instanceof ApiError) return err.message;
  return "Retry failed.";
}

function FireblocksEventDetailDialog({
  eventId,
  canRetry,
  onOpenChange,
}: {
  eventId: string | null;
  canRetry: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: ["fireblocks-event", eventId],
    queryFn: () => getFireblocksEvent(eventId!),
    enabled: eventId !== null,
    // feature-041: poll while the relay still owes this event an outcome (e.g. after a
    // manual retry),
    // then refresh the list and the sidebar badge so they show the outcome too.
    refetchInterval: (query) => {
      const status = query.state.data?.publishStatus;
      return status === FireblocksEventPublishStatus.PENDING || status === FireblocksEventPublishStatus.PROCESSING
        ? 3_000
        : false;
    },
  });
  const seen = useRef<{ id: string; status: FireblocksEventPublishStatus } | null>(null);
  useEffect(() => {
    if (!data) return;
    const prev = seen.current;
    if (prev && prev.id === data.id && prev.status !== data.publishStatus) {
      void queryClient.invalidateQueries({ queryKey: ["fireblocks-events"] });
      void queryClient.invalidateQueries({ queryKey: ["fireblocks-events-failed"] });
    }
    seen.current = { id: data.id, status: data.publishStatus };
  }, [data, queryClient]);

  // feature-041: hands a FAILED event back to the outbox relay. The detail,
  // the list and the sidebar badge all change, so all three are refreshed.
  const retry = useMutation({
    mutationFn: () => retryFireblocksEvent(eventId!),
    onSuccess: (detail) => {
      queryClient.setQueryData(["fireblocks-event", eventId], detail);
      toast.success("Retry queued — the event will be processed again within a few seconds.");
    },
    onError: (err) => toast.error(retryErrorMessage(err)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["fireblocks-event", eventId] });
      void queryClient.invalidateQueries({ queryKey: ["fireblocks-events"] });
      void queryClient.invalidateQueries({ queryKey: ["fireblocks-events-failed"] });
    },
  });

  return (
    <Dialog open={eventId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[95vh] max-w-3xl flex-col overflow-hidden">
        {isPending && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {isError && !isPending && (
          <div className="py-10 text-center text-sm text-muted-foreground">Failed to load event.</div>
        )}

        {data && !isPending && !isError && (
          <>
            <DialogHeader className="shrink-0">
              <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
                <DialogTitle>Fireblocks event</DialogTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge
                    label={formatStatusLabel(data.publishStatus)}
                    color={PUBLISH_STATUS_COLOR[data.publishStatus]}
                  />
                  <StatusBadge label={data.eventType} color={eventTypeColor(data.eventType)} />
                </div>
              </div>
              <DialogDescription asChild>
                <span className="flex items-center gap-1 font-mono text-xs">
                  {truncate(data.fireblocksEventId)}
                  <CopyButton value={data.fireblocksEventId} label="Copy event ID" className="h-4 w-4" />
                </span>
              </DialogDescription>
            </DialogHeader>

            <div className="shrink-0 border-b" />

            {data.lastPublishError && (
              <div
                className="flex shrink-0 items-start gap-2 rounded-md p-3 text-sm"
                style={{ backgroundColor: `${STATUS_DOT_COLOR.danger}0D`, color: STATUS_DOT_COLOR.danger }}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="break-words">{data.lastPublishError}</p>
              </div>
            )}

            {canRetry && data.publishStatus === FireblocksEventPublishStatus.FAILED && (
              <div className="flex shrink-0 items-center justify-between gap-3 rounded-md border border-border p-3">
                <p className="text-sm text-muted-foreground">Cause fixed? Process this event once more.</p>
                <Button size="sm" className="shrink-0" disabled={retry.isPending} onClick={() => retry.mutate()}>
                  <RotateCw className="h-4 w-4" />
                  {retry.isPending ? "Retrying…" : "Retry"}
                </Button>
              </div>
            )}

            <div className="grid shrink-0 grid-cols-1 gap-4 sm:grid-cols-2">
              <SectionCard title="Details">
                <dl className="space-y-2">
                  <DetailRow
                    label="Resource ID"
                    value={
                      data.resourceId ? (
                        <span className="flex items-center gap-1">
                          <span className="font-mono text-xs">{data.resourceId}</span>
                          <CopyButton value={data.resourceId} label="Copy resource ID" />
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <DetailRow label="Fireblocks asset ID" value={data.fireblocksAssetId ?? "—"} />
                  {/* blockHeight is a bigint-backed numeric string — rendered as plain text, never Number(...). */}
                  <DetailRow label="Block height" value={data.blockHeight ?? "—"} />
                </dl>
              </SectionCard>

              <SectionCard title="Timeline">
                <Timeline
                  entries={[
                    { label: "Fireblocks created", value: formatTimestamp(data.fireblocksCreatedAt) },
                    { label: "Received", value: formatTimestamp(data.receivedAt) },
                  ]}
                />
              </SectionCard>
            </div>

            <SectionCard title="Raw payload" className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <JsonViewer data={data.rawPayload} defaultCollapsed={false} fill />
            </SectionCard>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── row → dialog-opening click ─────────────────────────────────────────────

function RowButton({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onSelect} className="-m-2 block w-full p-2 text-left">
      {children}
    </button>
  );
}

function FireblocksEventsPage() {
  const { admin } = Route.useRouteContext();
  const tableState = useTableState<FireblocksEventFilters>({ defaultFilters: DEFAULT_FILTERS });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params: ListFireblocksEventsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["fireblocks-events", params],
    queryFn: () => listFireblocksEvents(params),
  });

  const columns: DataTableColumn<FireblocksEventListItem>[] = [
    {
      key: "fireblocksEventId",
      header: "Event ID",
      cell: (e) => (
        <span className="flex items-center gap-1">
          <RowButton onSelect={() => setSelectedId(e.id)}>
            <span className="font-mono text-xs">{truncate(e.fireblocksEventId)}</span>
          </RowButton>
          <CopyButton value={e.fireblocksEventId} label="Copy event ID" className="h-5 w-5 text-muted-foreground hover:text-foreground" />
        </span>
      ),
    },
    {
      key: "eventType",
      header: "Event type",
      filter: columnFilter(tableState, ["eventType"], (draft, set) => (
        <TextFilterField
          id="filter-event-type"
          label="Event type"
          placeholder="e.g. TRANSACTION_STATUS_UPDATED"
          value={draft.eventType}
          onChange={(v) => set("eventType", v)}
        />
      )),
      cell: (e) => (
        <RowButton onSelect={() => setSelectedId(e.id)}>
          <StatusBadge label={e.eventType} color={eventTypeColor(e.eventType)} />
        </RowButton>
      ),
    },
    {
      key: "resourceId",
      header: "Resource ID",
      filter: columnFilter(tableState, ["resourceId"], (draft, set) => (
        <TextFilterField
          id="filter-resource-id"
          label="Resource ID"
          value={draft.resourceId}
          onChange={(v) => set("resourceId", v)}
        />
      )),
      cell: (e) =>
        e.resourceId ? (
          <span className="flex items-center gap-1">
            <RowButton onSelect={() => setSelectedId(e.id)}>
              <span className="font-mono text-xs">{e.resourceId}</span>
            </RowButton>
            <CopyButton value={e.resourceId} label="Copy resource ID" className="h-5 w-5 text-muted-foreground hover:text-foreground" />
          </span>
        ) : (
          <RowButton onSelect={() => setSelectedId(e.id)}>
            <span className="text-muted-foreground">—</span>
          </RowButton>
        ),
    },
    {
      key: "publishStatus",
      header: "Status",
      filter: columnFilter(tableState, ["publishStatus"], (draft, set) => (
        <SelectFilterField
          id="filter-publish-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(FireblocksEventPublishStatus))}
          value={draft.publishStatus}
          onChange={(v) => set("publishStatus", v as FireblocksEventPublishStatus | undefined)}
        />
      )),
      cell: (e) => (
        <RowButton onSelect={() => setSelectedId(e.id)}>
          <StatusBadge label={formatStatusLabel(e.publishStatus)} color={PUBLISH_STATUS_COLOR[e.publishStatus]} />
        </RowButton>
      ),
    },
    {
      key: "fireblocksAssetId",
      header: "Asset ID",
      cell: (e) => <RowButton onSelect={() => setSelectedId(e.id)}>{e.fireblocksAssetId ?? "—"}</RowButton>,
    },
    {
      key: "blockHeight",
      header: "Block height",
      // Rendered as plain text, never Number(...) — bigint-backed numeric
      // string, per its own doc comment in api/types.ts.
      cell: (e) => (
        <RowButton onSelect={() => setSelectedId(e.id)}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{e.blockHeight ?? "—"}</span>
        </RowButton>
      ),
    },
    {
      key: "receivedAt",
      header: "Received",
      filter: columnFilter(tableState, ["from", "to"], (draft, set) => (
        <DateRangeFilterFields
          idPrefix="filter-date"
          from={draft.from}
          to={draft.to}
          onFromChange={(v) => set("from", v)}
          onToChange={(v) => set("to", v)}
        />
      )),
      cell: (e) => <RowButton onSelect={() => setSelectedId(e.id)}>{formatTimestamp(e.receivedAt)}</RowButton>,
    },
  ];

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        loading={isPending}
        error={isError ? "Failed to load Fireblocks events." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(e) => e.id}
        emptyMessage="No Fireblocks events match your filters."
      />

      <FireblocksEventDetailDialog
        eventId={selectedId}
        canRetry={hasRole(admin.roles, AdminRole.TECH_MANAGER)}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}
